/** @jsx jsx */
import { Fragment, useEffect, useRef, useState } from 'react'
import {
  Box,
  Container,
  Flex,
  Grid,
  Heading,
  jsx,
  Spinner,
  Text,
} from 'theme-ui'
import { useTranslate } from '@chordcommerce/gatsby-theme-autonomy'
import Pager from '~/components/Generic/Pager'
import Tabs from '~/components/Generic/Tabs'
import VisuallyHidden from '~/components/Generic/VisuallyHidden'
import Review from '~/components/Reviews/Review'
import ReviewStars from '~/components/Reviews/Stars'
import { useReviews } from '~/hooks/components/use-reviews'
import { roundReviewScore } from '~/utils/formatters'

const Reviews = ({ background, hideTitle, id, title }) => {
  const reviewsRef = useRef(null)
  const { getAllReviews, getAllStats, getProductReviews, getProductStats } =
    useReviews()

  const allReviews = getAllReviews()
  const allStats = getAllStats()
  const productReviews = !!id && getProductReviews(id)
  const productStats = !!id && getProductStats(id)

  const [tabs, setTabs] = useState([
    {
      slug: 'product',
      title: 'This product',
    },
    {
      slug: 'all',
      title: 'All reviews',
    },
  ])
  const [currentTab, setCurrentTab] = useState(tabs[0])
  const [hasProductReviews, setHasProductReviews] = useState(false)
  const [reviewsToShow, setReviewsToShow] = useState(null)
  const [statsToShow, setStatsToShow] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const { slug: currentTabSlug } = currentTab
  const reviewsPerPage = 9

  const onPagerClick = (page) => {
    if (page === currentPage) return

    setCurrentPage(page)
    reviewsRef.current.scrollIntoView()
  }

  const onTabChange = ({ slug: nextTabSlug }) => {
    if (nextTabSlug === currentTabSlug) return

    setCurrentTab(tabs.find(({ slug }) => slug === nextTabSlug))
    setCurrentPage(1)
  }

  useEffect(() => {
    setHasProductReviews(!!productReviews?.length)

    if (!!productReviews?.length && currentTabSlug === 'product') {
      setReviewsToShow(productReviews)
      setStatsToShow(productStats)
    } else {
      setReviewsToShow(allReviews)
      setStatsToShow(allStats)
    }
  }, [allReviews, allStats, currentTabSlug, productReviews, productStats])

  useEffect(() => {
    setTabs((tabs) => [
      { ...tabs[0] },
      { ...tabs[1], title: `All reviews (${allStats.count})` },
    ])
  }, [allStats])

  return (
    <Box
      bg={background || 'backgroundMedium'}
      pt={hideTitle ? 5 : [6, 7, null, 9]}
      pb={hideTitle ? [6, 9] : [7, 8, null, 10]}
    >
      <Box
        id="reviews"
        ref={reviewsRef}
        sx={{ position: 'relative', top: '-40px' }}
      />
      <Container>
        <ReviewsTitle hideTitle={hideTitle} title={title} />
        {hasProductReviews && (
          <Box mb={4}>
            <Tabs
              ariaLabel="Review category"
              currentOption={currentTab}
              options={tabs}
              setOption={onTabChange}
            />
          </Box>
        )}
        {statsToShow?.count && (
          <Box mb={5}>
            <ReviewsStats stats={statsToShow} />
          </Box>
        )}
        {!reviewsToShow && (
          <Box py={10} sx={{ textAlign: 'center' }}>
            <Spinner
              color="brandTertiary"
              size="100"
              mx="auto"
              sx={{
                '@media (prefers-reduced-motion)': {
                  circle: {
                    animationIterationCount: 8,
                  },
                },
              }}
            />
          </Box>
        )}
        {reviewsToShow && (
          <Box>
            <Grid columns={[null, 2, 3]} mb={5}>
              {reviewsToShow
                .slice(
                  reviewsPerPage * (currentPage - 1),
                  reviewsPerPage * currentPage
                )
                .map((review) => (
                  <Review
                    key={`review-${review.id}`}
                    review={review}
                    showListing={reviewsToShow === allReviews}
                  />
                ))}
            </Grid>
            <Pager
              currentPage={currentPage}
              onPagerClick={onPagerClick}
              totalPages={Math.floor(reviewsToShow.length / reviewsPerPage)}
            />
          </Box>
        )}
      </Container>
    </Box>
  )
}

const ReviewsTitle = ({ hideTitle, title }) => {
  const translate = useTranslate()

  return (
    <Fragment>
      {!hideTitle && (
        <Heading
          as="h2"
          mb={4}
          tabIndex="0"
          variant="title2"
          sx={{
            textAlign: 'center',
            '&:focus:not(:focus-visible)': { outline: 'none' },
          }}
        >
          {title || translate('product.reviews.title')}
        </Heading>
      )}
      {hideTitle && (
        <VisuallyHidden>
          <Heading as="h2">
            {title || translate('product.reviews.title')}
          </Heading>
        </VisuallyHidden>
      )}
    </Fragment>
  )
}

const ReviewsStats = ({ stats }) => {
  return (
    <Flex sx={{ alignItems: 'center', flexDirection: 'column' }}>
      <ReviewStars score={stats.score} />
      <Text as="p" variant="callout1" my={0}>
        {`${roundReviewScore(stats.score)}/5.0 (${stats.count} reviews)`}
      </Text>
    </Flex>
  )
}

export default Reviews
